/**
 * Button - shadcn/ui Button with Novala's brand.
 * Variants: default (green primary), outline, ghost, link, danger.
 * Sizes: default, sm, lg, icon.
 *
 * Disabled state is variant-aware: a disabled green button becomes a solid
 * gray fill with gray text (not faded green with washed-out white which
 * previously read as black text on murky green).
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md font-medium font-sans transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default: "bg-brand !text-white [&_svg]:!text-white hover:bg-brand-dark shadow-button disabled:bg-disabledBg disabled:!text-disabledText disabled:[&_svg]:!text-disabledText disabled:shadow-none",
        outline: "border border-stroke bg-white text-ink hover:bg-surface-cardActive disabled:text-disabledText",
        ghost: "text-ink hover:bg-surface-cardActive disabled:text-disabledText",
        link: "text-brand underline-offset-4 hover:underline disabled:text-disabledText disabled:no-underline",
        danger: "bg-danger !text-white [&_svg]:!text-white hover:bg-danger/90 disabled:bg-disabledBg disabled:!text-disabledText disabled:[&_svg]:!text-disabledText",
      },
      size: {
        default: "h-11 px-6 text-body",
        sm: "h-9 px-4 text-small",
        lg: "h-12 px-8 text-body",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, fullWidth, ...props }, ref) => {
  return <button className={cn(buttonVariants({ variant, size }), fullWidth && "w-full", className)} ref={ref} {...props} />;
});
Button.displayName = "Button";

export { Button, buttonVariants };
